import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Typography, Space } from 'antd';
import { useEffect, useState, useCallback, useMemo } from 'react';

import { Modal } from 'components/feedback/Modal';
import { Button } from 'components/general/Button';

const { Text, Title } = Typography;

interface SessionWarningModalProps {
  isVisible: boolean;
  timeRemaining: number;
  onExtendSession: () => void;
  onLogout: () => void;
}

export const SessionWarningModal = ({
  isVisible,
  timeRemaining,
  onExtendSession,
  onLogout,
}: SessionWarningModalProps) => {
  const [countdown, setCountdown] = useState(timeRemaining);

  useEffect(() => {
    if (!isVisible) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCountdown(timeRemaining);
  }, [isVisible, timeRemaining]);

  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0) {
          onLogout();
          return 0;
        }
        return prev - 1000; // Update every second
      });
    }, 1000); // 1 second interval

    return () => clearInterval(interval);
  }, [isVisible, onLogout]);

  const formatTime = useCallback((ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const formattedTime = useMemo(
    () => formatTime(countdown),
    [countdown, formatTime]
  );

  const handleExtendSession = useCallback(() => {
    onExtendSession();
    setCountdown(timeRemaining); // Reset countdown
  }, [onExtendSession, timeRemaining]);

  const handleLogout = useCallback(() => {
    onLogout();
  }, [onLogout]);

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined className="text-red-7" />
          <span>Session Expiring Soon</span>
        </Space>
      }
      open={isVisible}
      onCancel={handleLogout}
      footer={[
        <Button key="logout" onClick={handleLogout}>
          Logout Now
        </Button>,
        <Button key="extend" type="primary" onClick={handleExtendSession}>
          Extend Session
        </Button>,
      ]}
      closable={false}
      maskClosable={false}
      centered
    >
      <div className="space-y-4">
        <Title level={4}>Your session will expire in:</Title>

        <div className="text-center">
          <Text className="text-2xl font-bold text-red-7">{formattedTime}</Text>
        </div>

        <Text className="block text-secondary">
          For your security, you will be automatically logged out due to
          inactivity. Click &quot;Extend Session&quot; to continue working, or
          &quot;Logout Now&quot; to end your session immediately.
        </Text>

        <div className="bg-gray-100 p-3 rounded-md">
          <Text className="text-sm text-color-info">
            💡 Tip: Your session will be extended automatically when you
            interact with the application.
          </Text>
        </div>
      </div>
    </Modal>
  );
};
